import { startCase } from 'lodash'
import moment from 'moment'
import { timelinePendingOrAcceptedStatuses } from 'views/Resignations/resignationHelper'

import { CONFIRMATION_MODAL, FORM_MODAL } from 'actions/actionTypes'
import {
  createResignationApprovalStatusSuccess,
  updateResignationApprovalsSuccess,
  updateResignationTimelineSuccess
} from 'actions/resignations'
import { CustomInputWrapper, SelectWrapper } from 'components'
import CustomFieldsForm from 'components/Form/CustomFieldsForm'

import { checkPermission } from './permissionsHelper'

export const RESIGNATION_DATE_WINDOW_DAYS = 15

export const hasActiveResignation = (activeStatus) => timelinePendingOrAcceptedStatuses.includes(activeStatus?.status)

export const resignationsTabs = [
  {
    value: '/resignations/settings',
    label: 'Resignations Settings',
    href: '/resignations/settings'
  },
  {
    value: '/resignations/management',
    label: 'Resignations Management',
    href: '/resignations/management'
  }
]

export const renderResignationsActiveTab = (permissions, currentUser) => {
  const tabsPermissions = [
    canAccessResignationSettingTab(permissions),
    canAccessResignationMangementTab(currentUser, permissions)
  ]
  const activeTab = tabsPermissions.indexOf(true)
  return resignationsTabs[activeTab].href
}

export const isResignationTabs = (path, tabsPermissions) => {
  if ('/resignations/settings' === path && tabsPermissions[0]) return true
  if ('/resignations/management' === path && tabsPermissions[1]) return true
}

export const canAccessResignationPage = (currentUser, permissions) => {
  const canViewResignationSettings = canAccessResignationSettingTab(permissions)
  const canViewResignationManagement = canAccessResignationMangementTab(currentUser, permissions)

  return canViewResignationSettings || canViewResignationManagement
}

export const canAccessResignationMangementTab = (currentUser, permissions) => {
  const leadOrManagerCount = currentUser.subordinates_as_manager_count || currentUser.subordinates_as_lead_count
  const permissionCheck = checkPermission(permissions, 'resignations', 'general', 'resignation_management', true)

  const hasMyActionItems = permissionCheck.isMyActionItems()
  const hasFullAccess = permissionCheck.isFull()
  const isRestricted = permissionCheck.isRestricted()
  const isDepartmentModerator = permissionCheck.isDepartmentModerator()

  if (isRestricted || isDepartmentModerator || hasFullAccess) {
    return true
  } else if (hasMyActionItems) {
    return leadOrManagerCount > 0
  } else {
    return false
  }
}

export const canAccessResignationSettingTab = (permissions) => {
  const hasResignationSettingPermission = !checkPermission(
    permissions,
    'resignations',
    'general',
    'resignation_settings',
    true
  ).isNone()

  return hasResignationSettingPermission
}

export const handleApproval = (id, openModal, closeModal, createApprovalStatus, employee, isFromDetailPage = false) => {
  openModal(FORM_MODAL, {
    preventBackDropClose: true,
    form: (
      <CustomFieldsForm
        fields={[
          {
            heading: 'Comment (optional)',
            name: 'comment',
            component: CustomInputWrapper,
            inputProps: {
              multiline: true,
              placeholder: 'Enter comment here',
              rows: '3'
            }
          }
        ]}
        onSubmit={(values) =>
          createApprovalStatus(
            id,
            { comment: values.comment || '', from_detailed_page: isFromDetailPage },
            (payload) => {
              if (payload) {
                closeModal()
                return createResignationApprovalStatusSuccess(payload)
              }
            }
          )
        }
      />
    ),
    title: `Approving the request (${employee.name} - ${employee.id})`
  })
}

export const handleChangeApprover = (approvalId, openModal, closeModal, updateApproval, employee) => {
  openModal(FORM_MODAL, {
    preventBackDropClose: true,
    form: (
      <CustomFieldsForm
        gridClasses={'flex-column'}
        fields={[
          {
            heading: 'New Approver*',
            name: 'approver_id',
            component: SelectWrapper,
            userSearchable: true,
            placeholder: 'Select employee'
          }
        ]}
        onSubmit={(values) => {
          updateApproval(approvalId, { approver_type: 'User', approver_id: values.approver_id }, (payload) => {
            if (payload) {
              closeModal()
              return updateResignationApprovalsSuccess(payload)
            }
          })
        }}
      />
    ),
    title: `Change Approver (${employee.name} - ${employee.id})`
  })
}

export const handleReinitiateApproval = (timelineId, openModal, updateResignationTimeline, employee) => {
  openModal(CONFIRMATION_MODAL, {
    onConfirmation: () =>
      updateResignationTimeline(timelineId, { reinitiate: true }, (payload) => {
        if (payload) return updateResignationTimelineSuccess(payload)
      }),
    resourceType: 'Resignation Approval',
    alertModal: true,
    title: 'Are you sure you want to reinitiate this approval?',
    message: `All fields will be cleared and approval will restart from the initial stage for ${employee.name} - ${employee.id}.`
  })
}

export const handleArchiveResignation = (timelineId, openModal, archiveResignationTimeline, employee) => {
  openModal(CONFIRMATION_MODAL, {
    onConfirmation: () =>
      archiveResignationTimeline(timelineId, { status: 'archived' }, (payload) => {
        if (payload) return updateResignationTimelineSuccess(payload)
      }),
    resourceType: 'Resignation',
    alertModal: true,
    title: 'Are you sure you want to archive this resignation?',
    message: `The resignation for ${employee.name} - ${employee.id} will move to Archive and all pending approvals will be discarded.`
  })
}

const resignationApproverTypes = {
  Lead: { label: 'Lead', person: true },
  Pm: { label: 'Project Manager', person: true },
  ClusterHead: { label: 'Cluster Head', person: true },
  User: { label: 'User', person: true },
  Department: { label: 'Department' },
  Role: { label: 'Role Based Approver' }
}

export const isPersonResignationApprover = (approverType) => !!resignationApproverTypes[approverType]?.person

export const resignationApproverDesignation = (approverType) =>
  resignationApproverTypes[approverType]?.label || startCase(approverType)

export const resignationApproverSummary = (approver, approverType) => {
  const designation = resignationApproverDesignation(approverType)

  if (!approver?.name) return `${designation} (Not Assigned)`
  if (!isPersonResignationApprover(approverType)) return `${designation} (${approver.name})`

  return `${approver.name}${approver.id ? ` (ID-${approver.id})` : ''} - ${designation}`
}

export const combineDateAndTime = (datePart, timePart) => {
  if (!datePart) return null

  const combined = moment(datePart)
  if (timePart) {
    const time = moment(timePart)
    combined.set({ hour: time.hour(), minute: time.minute(), second: 0 })
  }

  return combined.format('YYYY-MM-DD HH:mm')
}
